// src/components/pages/admin-dash/DrawForm.jsx
import React, { useMemo, useState, useEffect } from "react";
import Card from "../../ui/Card";
import Button from "../../ui/Button";
import Field from "../../ui/Field";
import Icon from "../../ui/Icon";
import Badge from "../../ui/Badge";

/**
 * Shared form for creating/editing a draw. Matches the real backend
 * contract (`DrawRequestDto`): typeLottery, moonPhase, date, value,
 * num1..num5, extraBall — NOT the drawNumber/isAccumulated/winningNumbers[]
 * shape this screen used to send (that never matched the backend and
 * 400'd on `moonPhase` every time — see 1-ADMIN_ROADMAP.md item 2.1).
 *
 * Pure presentational form: the parent (DrawCreate / DrawEdit) owns the
 * API call and passes `initialValue` + `onSubmit`.
 */

const LOTTERIES = [
  { key: "MEGA_MILLIONS", label: "Mega Millions", mainMax: 70, specialName: "Mega Ball", specialMax: 25 },
  { key: "POWER_BALL",    label: "Powerball",      mainMax: 69, specialName: "Power Ball", specialMax: 26 },
];

const MOON_PHASES = [
  "FIRSTQUARTER", "FULLMOON", "NEWMOON", "THIRDQUARTER",
  "WANINGCRESCENT", "WANINGGIBBOUS", "WAXINGCRESCENT", "WAXINGGIBBOUS",
];

const moonLabel = (m) =>
  m.charAt(0) + m.slice(1).toLowerCase().replace(/(quarter|moon|crescent|gibbous)/, " $1").trim();

const todayIso = () => new Date().toISOString().split("T")[0];

const emptyValue = {
  typeLottery: "MEGA_MILLIONS",
  moonPhase: "",
  date: todayIso(),
  value: "",
  num1: "", num2: "", num3: "", num4: "", num5: "",
  extraBall: "",
};

export default function DrawForm({ initialValue, submitLabel = "Save", submitting = false, onSubmit }) {
  const [form, setForm] = useState({ ...emptyValue, ...initialValue });

  // Re-sync when a new initialValue arrives (edit mode, after the fetch resolves).
  useEffect(() => {
    if (initialValue) setForm({ ...emptyValue, ...initialValue });
  }, [initialValue]);

  const meta = useMemo(
    () => LOTTERIES.find((l) => l.key === form.typeLottery) || LOTTERIES[0],
    [form.typeLottery]
  );

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const setLottery = (key) => setForm((f) => ({ ...f, typeLottery: key }));
  const setMoon = (key) => setForm((f) => ({ ...f, moonPhase: key }));

  /* ------------------------- Validation ----------------------------- */
  const errors = {};
  if (!form.date) errors.date = "Required.";
  if (!form.moonPhase) errors.moonPhase = "Required.";

  const valNum = Number(form.value);
  if (form.value === "" || !Number.isFinite(valNum) || valNum <= 0)
    errors.value = "Enter a positive jackpot amount.";

  const mainKeys = ["num1", "num2", "num3", "num4", "num5"];
  const mainParsed = mainKeys.map((k) => Number(form[k]));
  const allMainFilled = mainKeys.every((k) => form[k] !== "" && form[k] !== null);
  const mainInRange = mainParsed.every((n) => Number.isFinite(n) && n >= 1 && n <= meta.mainMax);
  const mainDistinct = new Set(mainParsed).size === mainParsed.length;
  if (!allMainFilled)     errors.main = "Fill in all 5 numbers.";
  else if (!mainInRange)  errors.main = `Each number must be 1–${meta.mainMax}.`;
  else if (!mainDistinct) errors.main = "Main numbers must be unique.";

  const extraNum = Number(form.extraBall);
  if (form.extraBall === "" || !Number.isFinite(extraNum))
    errors.extraBall = "Required.";
  else if (extraNum < 1 || extraNum > meta.specialMax)
    errors.extraBall = `Must be 1–${meta.specialMax}.`;

  const valid = Object.keys(errors).length === 0;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!valid) return;
    onSubmit({
      typeLottery: form.typeLottery,
      moonPhase: form.moonPhase,
      date: form.date,
      value: valNum,
      num1: mainParsed[0], num2: mainParsed[1], num3: mainParsed[2],
      num4: mainParsed[3], num5: mainParsed[4],
      extraBall: extraNum,
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <Card className="space-y-1">
        {/* Lottery picker */}
        <div className="mb-3.5">
          <label className="block text-[12px] font-medium mb-1.5 tracking-wide" style={{ color: "var(--txt)" }}>
            Lottery
          </label>
          <div className="flex flex-wrap gap-2">
            {LOTTERIES.map((l) => (
              <Chip key={l.key} active={form.typeLottery === l.key} onClick={() => setLottery(l.key)} label={l.label} />
            ))}
          </div>
        </div>

        {/* Moon phase picker — required by the backend (Draw.moonPhase is @NotNull) */}
        <div className="mb-3.5">
          <label className="block text-[12px] font-medium mb-1.5 tracking-wide" style={{ color: "var(--txt)" }}>
            Moon phase
          </label>
          <div className="flex flex-wrap gap-2">
            {MOON_PHASES.map((m) => (
              <Chip key={m} active={form.moonPhase === m} onClick={() => setMoon(m)} label={moonLabel(m)} small />
            ))}
          </div>
          {errors.moonPhase && (
            <p className="mt-2 text-[11px] flex items-center gap-1" style={{ color: "var(--red)" }}>
              <Icon name="alert-circle" size={11} /> {errors.moonPhase}
            </p>
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Date" type="date" value={form.date} onChange={set("date")} error={errors.date} />
          <Field
            label="Jackpot (USD)"
            type="number"
            min={0}
            value={form.value}
            onChange={set("value")}
            placeholder="e.g. 340000000"
            error={errors.value}
          />
        </div>

        {/* Winning numbers */}
        <div className="mb-2">
          <label className="block text-[12px] font-medium mb-1.5 tracking-wide" style={{ color: "var(--txt)" }}>
            Winning numbers
          </label>
          <p className="text-[11px] mb-2" style={{ color: "var(--txt-3)" }}>
            5 main numbers (1–{meta.mainMax}) + the {meta.specialName} (1–{meta.specialMax}).
          </p>

          <div className="flex flex-wrap items-center gap-2">
            {mainKeys.map((k, i) => (
              <NumInput key={k} value={form[k]} onChange={set(k)} min={1} max={meta.mainMax} ariaLabel={`Main number ${i + 1}`} />
            ))}
            <span className="px-1 font-semibold" style={{ color: "var(--txt-3)" }}>+</span>
            <NumInput value={form.extraBall} onChange={set("extraBall")} min={1} max={meta.specialMax} ariaLabel={meta.specialName} tone="gold" />
            <Badge variant="gold">{meta.specialName}</Badge>
          </div>

          {errors.main && (
            <p className="mt-2 text-[11px] flex items-center gap-1" style={{ color: "var(--red)" }}>
              <Icon name="alert-circle" size={11} /> {errors.main}
            </p>
          )}
          {errors.extraBall && !errors.main && (
            <p className="mt-2 text-[11px] flex items-center gap-1" style={{ color: "var(--red)" }}>
              <Icon name="alert-circle" size={11} /> {meta.specialName}: {errors.extraBall}
            </p>
          )}
        </div>
      </Card>

      <div className="flex items-center justify-end gap-2 mt-4">
        <Button icon="check" size="sm" type="submit" loading={submitting} disabled={!valid}>{submitLabel}</Button>
      </div>
    </form>
  );
}

function Chip({ label, active, onClick, small }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full font-medium transition-colors ${small ? "px-3 py-1 text-[11.5px]" : "px-4 py-2 text-[13px]"}`}
      style={{
        background: active ? "var(--gold)" : "var(--surf-2)",
        color:      active ? "#0E2A18"     : "var(--txt-2)",
        border: `1px solid ${active ? "var(--gold)" : "var(--border-2)"}`,
      }}
    >
      {active && <Icon name="check" size={small ? 11 : 13} />}
      {label}
    </button>
  );
}

function NumInput({ value, onChange, min, max, ariaLabel, tone = "default" }) {
  const gold = tone === "gold";
  return (
    <input
      type="number"
      value={value}
      onChange={onChange}
      min={min}
      max={max}
      aria-label={ariaLabel}
      className="text-center font-semibold tabular-nums outline-none transition-colors"
      style={{
        width: 50,
        height: 50,
        borderRadius: 999,
        fontSize: 15,
        background: gold ? "var(--gold-dim)" : "var(--surf-2)",
        color: gold ? "var(--gold)" : "var(--txt)",
        border: `1px solid ${gold ? "var(--gold-border)" : "var(--border-2)"}`,
      }}
    />
  );
}
